import React from "react";
import { Box, CircularProgress } from "@mui/joy";
import { useQualification } from "../../../contexts/api";
import { useConfirmationModal } from "../../../contexts/app/confirmation";
import { Permission, usePermissions } from "../../../contexts/app/permissions";
import type {
  QualificationItem,
  QualificationDraft,
} from "../../../api/qualification/Qualification.typed";
import DarkTypography from "../../common/DarkTypography";
import DarkCard from "../../common/DarkCard";
import QualificationsToolbar from "./display/QualificationsToolbar";
import QualificationsTable from "./display/QualificationsTable";
import { textTokens } from "../../../styles/globalStyles";

const emptyDraft: QualificationDraft = { sigle: "", name: "", description: "" };

export default function QualificationsPage() {
  const {
    listQualificationItems,
    createQualificationItem,
    updateQualificationItem,
    deleteQualificationItem,
  } = useQualification();
  const { hasPermission } = usePermissions();
  const canView = hasPermission(Permission.CUSTOMERS_QUALIFICATIONS_VIEW);
  const canEdit = hasPermission(Permission.CUSTOMERS_QUALIFICATIONS_EDIT);
  const { showConfirmation } = useConfirmationModal();

  const [items, setItems] = React.useState<QualificationItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [draft, setDraft] = React.useState<QualificationDraft>(emptyDraft);

  const load = React.useCallback(async () => {
    setLoading(true);
    const list = await listQualificationItems();
    setItems(list ?? []);
    setLoading(false);
  }, [listQualificationItems]);

  React.useEffect(() => {
    load();
  }, [load]);

  const startAdd = React.useCallback(() => {
    setEditingId(0);
    setDraft(emptyDraft);
  }, []);

  const startEdit = React.useCallback((item: QualificationItem) => {
    setEditingId(item.id);
    setDraft({
      sigle: item.sigle ?? "",
      name: item.name ?? "",
      description: item.description ?? "",
    });
  }, []);

  const cancelEdit = React.useCallback(() => {
    setEditingId(null);
    setDraft(emptyDraft);
  }, []);

  const onDraftChange = React.useCallback((patch: Partial<QualificationDraft>) => {
    setDraft((prev: QualificationDraft) => ({ ...prev, ...patch }));
  }, []);

  const save = React.useCallback(async () => {
    const sigle = draft.sigle.trim();
    if (!sigle) return;
    const ok = await showConfirmation(
      editingId === 0 ? "Créer la qualification ?" : "Enregistrer la modification ?",
      editingId === 0 ? "Un nouveau sigle sera créé." : "Les changements seront sauvegardés.",
    );
    if (!ok) return;
    const payload = {
      sigle,
      name: draft.name.trim() || undefined,
      description: draft.description.trim() || undefined,
    };
    try {
      if (editingId === 0) {
        await createQualificationItem(payload);
      } else if (editingId !== null) {
        await updateQualificationItem(editingId, payload);
      }
      await load();
      cancelEdit();
    } catch {
      // Alert handled in context
    }
  }, [
    showConfirmation,
    createQualificationItem,
    draft,
    editingId,
    load,
    cancelEdit,
    updateQualificationItem,
  ]);

  const remove = React.useCallback(
    async (id: number) => {
      const ok = await showConfirmation(
        "Supprimer cette qualification ?",
        "Cette action est irréversible.",
      );
      if (!ok) return;
      try {
        await deleteQualificationItem(id);
        await load();
      } catch {
        // Alert handled in context
      }
    },
    [showConfirmation, deleteQualificationItem, load],
  );

  if (!canView) {
    return (
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 120,
        }}
      >
        <DarkTypography level="body-md" colorVariant="secondary">
          Accès refusé
        </DarkTypography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2 },
        height: "100%",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size="md" color="primary" />
        </Box>
      )}
      {!loading && (
        <DarkCard dense>
          <QualificationsToolbar
            canEdit={canEdit}
            loading={loading}
            isEditing={editingId !== null}
            onAdd={startAdd}
          />
          {items.length === 0 && editingId !== 0 && (
            <DarkTypography level="body-md" sx={{ color: textTokens.color.dimmed, mb: 1 }}>
              Aucune qualification configurée.
            </DarkTypography>
          )}
          <QualificationsTable
            items={items}
            editingId={editingId}
            draft={draft}
            loading={loading}
            canEdit={canEdit}
            onDraftChange={onDraftChange}
            onSave={save}
            onCancelEdit={cancelEdit}
            onStartEdit={startEdit}
            onRemove={remove}
          />
        </DarkCard>
      )}
    </Box>
  );
}
